import { cx } from "class-variance-authority";
import React, { useEffect } from "react";
import { RiArrowUpDownLine } from "react-icons/ri";
import {
  ControlProps,
  InputProps,
  OptionProps,
  components,
} from "react-select";
import { SelectOption, _useSelectAriaActiveDescendantContext } from "./base";

interface RefWrapperProps {
  children: React.ReactNode;
  className: string;
}
/**
 * Aid for positioning the popover.
 */
export const _RefDivWrapper = React.forwardRef<HTMLDivElement, RefWrapperProps>(
  (props, ref) => {
    if (ref)
      return (
        <div ref={ref} className={props.className}>
          {props.children}
        </div>
      );
    return <>{props.children}</>;
  },
);

const findOption = (inputValue: string, option: { label?: string }) => {
  if (!option.label) return false;
  return option?.label
    .toLocaleUpperCase()
    .includes((inputValue ?? "").toLocaleUpperCase());
};
const onKeyDown = (
  e: React.KeyboardEvent<HTMLInputElement>,
  props: InputProps<SelectOption>,
) => {
  // React-Select does not expose an easy way to use the keydown event
  // to prevent the enter key from firing the onChange event when nothing is selected
  // this causes the form to submit when the user hits enter in the input and nothing is selected
  // so we manually search for the option and prevent the event if it is not found
  if (e.key === "Enter") {
    const hasOption = props.options.some((o) =>
      findOption(props?.value?.toString() ?? "", o),
    );
    if (!hasOption) {
      e.preventDefault();
      e.stopPropagation();
    }
  }
};

/**
 * Applies `Enter` key and accessibility fixes.
 */
export const InputWithEnterKeyFix = (props: InputProps<SelectOption>) => {
  const {
    ariaActiveDescendant: ariaActiveDescendantPair,
    isRequired,
    id,
    ariaLabelledBy,
    ariaDescribedBy,
  } = _useSelectAriaActiveDescendantContext();
  const [ariaActiveDescendant] = ariaActiveDescendantPair;

  // Not able to put aria-activedescendant directly on component.
  const enhancedProps: InputProps<SelectOption> = {
    ...props,
    // Only for identifying the active item in the popover--no need to render when menu is closed.
    "aria-activedescendant": props.selectProps.menuIsOpen
      ? ariaActiveDescendant
      : undefined,
    "aria-labelledby": ariaLabelledBy,
    "aria-describedby": ariaDescribedBy,
  };

  return (
    <components.Input
      {...enhancedProps}
      aria-required={isRequired}
      id={id}
      aria-describedby={ariaDescribedBy}
      onKeyDown={(e) => onKeyDown(e, props)}
    />
  );
};

/**
 * Applies accessiblity fixes.
 */
export const AccessibleOption = (props: OptionProps<SelectOption>) => {
  const { ariaActiveDescendant: ariaActiveDescendantPair } =
    _useSelectAriaActiveDescendantContext();
  const [_, setAriaActiveDescendant] = ariaActiveDescendantPair;
  const { isFocused, innerProps } = props;
  const { id } = innerProps;

  // https://reactjs.org/blog/2020/02/26/react-v16.13.0.html#warnings-for-some-updates-during-render
  useEffect(() => {
    if (isFocused) setAriaActiveDescendant(id ?? "");
  }, [isFocused, id, setAriaActiveDescendant]);

  return <components.Option {...props}>{props.children}</components.Option>;
};

/**
 * Applies icon to sort dropdowns.
 */
export const SortControl = (props: ControlProps<SelectOption>) => {
  return (
    <components.Control {...props}>
      <RiArrowUpDownLine
        size={16}
        className={cx("ml-3.5 flex items-center justify-center")}
      />
      {props.children}
    </components.Control>
  );
};
